import { spotifyBridge } from "./spotify"
import type { BridgeController, ProviderBridge } from "./types"
import { youtubeBridge } from "./youtube"

const bridges: ProviderBridge[] = [youtubeBridge, spotifyBridge]

/**
 * Live controllers by iframe. `ContentPlayer.LiveIframe` attaches bridges but
 * keeps the controller private; shells sometimes need to drive the SAME
 * controller (e.g. unmuting in place at a jacket threshold — flipping the
 * `muted` prop would rebuild the iframe src and reload the embed, losing
 * playback position). Registration happens inside `findBridge`'s wrapper so
 * every current and future bridge participates without changes.
 */
const liveControllers = new WeakMap<HTMLIFrameElement, BridgeController>()

export function findBridge(embedUrl: string): ProviderBridge | undefined {
  const bridge = bridges.find((b) => b.matches(embedUrl))
  if (!bridge) return undefined
  return {
    matches: (url) => bridge.matches(url),
    attach: (iframe) => {
      const controller = bridge.attach(iframe)
      liveControllers.set(iframe, controller)
      return {
        ...controller,
        destroy: () => {
          liveControllers.delete(iframe)
          controller.destroy()
        },
      }
    },
  }
}

/**
 * The live bridge controller for the (single) bridged iframe under `root`,
 * if any. Shells pass their slide/container element; returns null when the
 * active embed has no bridge (Spotify, Vimeo, TikTok, passthrough).
 */
export function findLiveController(
  root: HTMLElement | null,
): BridgeController | null {
  if (!root) return null
  for (const el of root.querySelectorAll("iframe")) {
    const controller = liveControllers.get(el)
    if (controller) return controller
  }
  return null
}

export type { BridgeController, ProviderBridge } from "./types"
